<template>
  <section class="overflow-auto p-3 pretty-scroll-theme">
    <div class="text-white float-end hstack gap-1">
      <template v-if='votingRoundReport'>
        <a class="btn btn-primary" target='_blank' :href='`../voting_rounds/${votingRound.id}/voting_round_reports/${votingRoundReport?.id}`'>
          <AVIcon icon="external-link" class="me-1" />
          {{ $t("js.backend.live.results.result_report") }}
        </a>
      </template>
    </div>
    <header :title="votingRound.reference">
      <div class="h1 mb-0">{{ label }}</div>
      <div class="mb-3">
        <span class="badge text-bg-primary" v-if="votingRoundReport && votingRoundReport.publishedAt">
          <span v-text="$t('js.backend.live.voting_round_report.published')"></span>:
          <time :datetime="votingRoundReport.publishedAt"
            v-text="new Date(votingRoundReport.publishedAt).toLocaleString()"></time>
        </span>
        <span class="badge text-bg-warning" v-else v-text="$t('js.backend.live.voting_round_report.not_published')"></span>
      </div>
    </header>
    <dl style="display: grid; grid-auto-columns: 1fr; grid-template-rows: auto auto; grid-auto-flow: column">
      <dt v-text="openedAtLabel"></dt>
      <dd>
        <time v-if="openedAtDate" :datetime="votingRound.openedAt"
          v-text="openedAtDisplay"></time>
        <span v-else class="text-white-50">{{ openedAtDisplay }}</span>
      </dd>
      <dt v-text="closedAtLabel"></dt>
      <dd>
        <time v-if="closedAtDate" :datetime="votingRound.closedAt"
          v-text="closedAtDisplay"></time>
        <span v-else class="text-white-50">{{ closedAtDisplay }}</span>
      </dd>
      <dt>{{ $t("activerecord.models.contest", { n: 10 }) }}</dt>
      <dd class='overflow-auto' style='max-height: 80px'>
        <template v-if="contestsInRound">
          <p v-for="contest in contestsInRound" class="badge text-bg-secondary mb-0 w-100">
            {{ contest.title[firstAvailableLocale] }}
          </p>
        </template>
        <span v-else class="text-white-50">[ blank ]</span>
      </dd>
    </dl>

    <!-- Accordion -->
    <div id="accordionVotingRound" class="accordion accordion-dark">

      <!-- Results -->
      <div class="accordion-item">
        <h2 class="accordion-header" id="headingResult">
          <button class="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#collapseResult"
            aria-expanded="true" aria-controls="collapseResult">
            {{ $t("js.backend.live.voting_round_report.results") }}
          </button>
        </h2>
        <div id="collapseResult" class="bg-blur-dark accordion-collapse collapse show" aria-labelledby="headingResult"
          data-parent="#accordionVotingRound">
          <template v-if="votingRoundReport && currentState === 'closed'">
            <ResultCeremony :key='votingRoundReport.id'
                            v-if='votingRoundReport'
                            :report-id='votingRoundReport.id'
                            :orchestrator-id='votingRoundReport.orchestratorId'
                            class='text-brand-dark'
                            :refresh-on-finish='false'
            />
          </template>
          <div v-else class="p-3">
            <p v-if="currentState === 'open'">{{ $t("js.backend.live.voting_round_report.voting_round_open") }}</p>
            <p v-else-if="currentState === 'future'">{{ $t("js.backend.live.voting_round_report.voting_round_future") }}
            </p>
            <template v-else-if="!votingRoundReport">
              <p v-if="votingRound.voteCount === 0">{{ $t("js.backend.live.voting_round_report.no_votes") }}</p>
              <p v-else>{{ $t("js.backend.live.voting_round_report.no_report") }}</p>
            </template>
          </div>
        </div>
      </div>

      <!-- Statistics -->
      <div class="accordion-item" v-if="election.statisticsClientUrl">
        <h2 class="accordion-header" id="headingStats">
          <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse"
            data-bs-target="#collapseStats" aria-expanded="true" aria-controls="collapseStats">
            {{ $t("js.backend.live.statistics") }}
          </button>
        </h2>
        <div id="collapseStats" class="accordion-collapse collapse" aria-labelledby="headingStats"
          data-parent="#accordionVotingRound">
          <div>
            <Statistics :statisticsUrl="statisticsUrl" />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import IndeterminateSpinner from "./IndeterminateSpinner.vue";
import VotingRoundResult from "./VotingRoundResult.vue";
import { mapState } from "pinia";
import { calculateState, label } from "@/entrypoints/shared/voting_rounds_helper";
import AsyncButton from "@/components/shared/AsyncButton.vue";
import Statistics from "./Statistics.vue"
import TurnoutTable from "./TurnoutTable.vue";
import { orderContests } from "@/entrypoints/shared/contest_utilities";
import { useSharedStore } from "@/entrypoints/stores/shared";
import { usePresentationStore } from "@/entrypoints/stores/presentation";
import type {
  ConferenceContest,
  ConferenceVotingRound,
  PropType,
  Theme,
  ConferenceVotingRoundReport,
  Result,
} from "@/types";
import ResultOrchestrator from '@/components/backend/ResultOrchestrator.vue';
import ResultCeremony from '@/components/backend/ResultCeremony.vue';

export default defineComponent({
  name: "VotingRoundReport",
  components: { ResultCeremony, ResultOrchestrator, TurnoutTable, AsyncButton, IndeterminateSpinner, VotingRoundResult, Statistics },
  props: {
    votingRound: {
      type: Object as PropType<ConferenceVotingRound>,
      required: true,
    },
    showTurnout: {
      type: Boolean,
      default: true,
    },
    theme: {
      type: String as PropType<Theme>,
      default: "dark",
    },
  },
  computed: {
    ...mapState(useSharedStore, ["firstAvailableLocale", "election"]),
    ...mapState(usePresentationStore, ["contests", "votingRoundReports"]),
    label() {
      return label(this.votingRound, this.$i18n.locale);
    },
    currentState() {
      return calculateState(this.votingRound);
    },
    contestsInRound() {
      const filteredContests = this.contests.filter((c: ConferenceContest) =>
        this.votingRound.contestReferences.includes(c.reference));
      return this.orderContests(filteredContests, this.votingRound.contestPositions);
    },
    closedAtLabel() {
      const closedAt = this.votingRound.closedAt;
      if (closedAt == null || new Date(closedAt).getTime() < Date.now()) {
        return this.$t("js.backend.live.voting_rounds_sheet.closed_at_time.past");
      } else {
        return this.$t("js.backend.live.voting_rounds_sheet.closed_at_time.future");
      }
    },
    openedAtLabel() {
      const openedAt = this.votingRound.openedAt;
      if (openedAt == null || new Date(openedAt).getTime() < Date.now()) {
        return this.$t("js.backend.live.voting_rounds_sheet.opened_at_time.past");
      } else {
        return this.$t("js.backend.live.voting_rounds_sheet.opened_at_time.future");
      }
    },
    openedAtDate() {
      return this.parseDate(this.votingRound.openedAt);
    },
    openedAtDisplay() {
      return this.formattedDate(this.openedAtDate);
    },
    votingRoundReport(): ConferenceVotingRoundReport {
      return this.votingRoundReports?.find((vrr: ConferenceVotingRoundReport) =>
        vrr.votingRoundReference === this.votingRound.reference);
    },
    closedAtDate() {
      return this.parseDate(this.votingRound.closedAt);
    },
    closedAtDisplay() {
      return this.formattedDate(this.closedAtDate);
    },
    statisticsUrl() {
      return `${this.election.statisticsClientUrl}/${this.votingRound.reference}/tables/overview`;
    },
    resultOrchestrator() {
      return this.votingRoundReport.resultOrchestrators[this.votingRoundReport.resultOrchestrators.length - 1];
    },
    turnout() {
      return (this.resultOrchestrator.result as Result).voterCounts;
    },
    orderedOrchestrators() {
      const contestPositions = this.votingRound.contestPositions
      return [...this.votingRoundReport.resultOrchestrators].sort((a, b) =>
        contestPositions[a.contestReference] - contestPositions[b.contestReference]
      );
    }
  },
  methods: {
    orderContests,
    parseDate(value: string | null | undefined) {
      if (!value) return null;

      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    },
    formattedDate(date: Date | null) {
      return date ? this.$d(date, "long") : "[ blank ]";
    },
  },
});
</script>

<style>
.accordion-dark {
  --bs-accordion-bg: transparent;
  --bs-accordion-color: var(--bs-white);
  --bs-accordion-active-bg: var(--av-theme-background);
  --bs-accordion-active-color: var(--av-theme-text);
  --bs-accordion-border-color: #9B9F9B;
  --bs-accordion-btn-bg: rgba(30, 30, 30, 0.8);
  --bs-accordion-btn-color: var(--bs-light);
  --bs-accordion-btn-focus-border-color: #000000;
  --bs-accordion-btn-focus-box-shadow: 0 0 0 0.25rem rgba(0, 0, 0, 0.25);
  --bs-accordion-btn-icon: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23818181'%3e%3cpath fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3e%3c/svg%3e");
  --bs-accordion-btn-active-icon: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23b2b2b2'%3e%3cpath fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3e%3c/svg%3e");
}
</style>
